import { NavLink } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  DollarSign, 
  Settings,
  BarChart3,
  UserCircle,
  Menu,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import SignOutButton from "./SignOutButton";
import { ThemeToggle } from "./ui/theme-toggle";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

interface SidebarProps {
  defaultActiveTab?: string;
}

const Sidebar = ({ defaultActiveTab = "dashboard" }: SidebarProps) => {
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const { hasFullAccess, hasBuAccess } = useAuth();
  const isLightMode = theme === 'light';
  
  // Colored icons based on theme
  const iconColors = {
    users: "#9b87f5", // purple for KOL Tracker
    userCircle: "#0EA5E9", // blue for KOL Hub
    layoutDashboard: "#33e1ed", // teal for Expense/Reach Overview
    barChart3: "#F97316", // orange for KOL's Reports
    dollarSign: "#D946EF", // magenta for Integration
    settings: "#8E9196", // gray for Settings
    menu: isLightMode ? "#333333" : "#f1f1f1" // neutral for menu
  };
  
  const navItems = [
    { name: "KOL Tracker", icon: Users, path: "/kol-tracker", color: iconColors.users },
    ...(hasFullAccess ? [
      { name: "KOL Hub", icon: UserCircle, path: "/kol-compensation", color: iconColors.userCircle }
    ] : []),
    { name: "Expense/Reach Overview", icon: LayoutDashboard, path: "/global-reach", color: iconColors.layoutDashboard },
    ...(hasFullAccess || hasBuAccess ? [
      { name: "KOL's Reports", icon: BarChart3, path: "/analytics", color: iconColors.barChart3 }
    ] : []),
    ...(hasFullAccess ? [
      { name: "Integration", icon: DollarSign, path: "/integration-settings", color: iconColors.dollarSign }
    ] : []),
    // Show Settings to both full-access and limited users
    { name: "Settings", icon: Settings, path: "/settings", color: iconColors.settings }
  ];

  const NavigationLinks = () => (
    <nav className="flex-1 flex items-center justify-center space-x-8">
      {navItems.map((item) => (
        <NavLink 
          key={item.name} 
          to={item.path}
          className={({ isActive }) => cn(
            "flex items-center p-2 rounded-sm transition-colors duration-200 relative group",
            isActive 
              ? "text-black dark:text-white font-semibold" 
              : "text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white"
          )}
        >
          <item.icon className="h-5 w-5" color={item.color} />
          <span className="ml-2 font-opensans text-sm tracking-wider">{item.name}</span>
          <span className={cn(
            "absolute bottom-0 left-0 w-full h-0.5 scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left",
            "dark:bg-white light:bg-black"
          )}></span>
        </NavLink>
      ))}
    </nav>
  );

  const Logo = () => (
    <div className="flex items-center">
      <div className="h-16 w-20">
        {isLightMode ? (
          <img 
            src="/lovable-uploads/ef8f3356-2693-4a98-8bc7-d4b32fbe8397.png" 
            alt="Mantle Logo Light" 
            className="h-full w-full object-contain" 
          />
        ) : (
          <img 
            src="/lovable-uploads/57a7aaa6-bf38-4409-a65a-b5c45e8026c6.png" 
            alt="Mantle Logo Dark" 
            className="h-full w-full object-contain"
          />
        )}
      </div>
      <Separator orientation="vertical" className="mx-4 h-6 dark:bg-white/10 light:bg-gray-300" />
      <h1 className="font-opensans font-bold tracking-wider text-black dark:text-dashboard-text-primary">
        KOL MANAGEMENT SYSTEM
      </h1>
    </div>
  );

  if (isMobile) {
    return (
      <div className="w-full backdrop-blur-md bg-white/80 dark:bg-[rgba(26,26,26,0.8)] border-b border-gray-200 dark:border-white/5 px-4 py-2 flex items-center justify-between transition-colors duration-200">
        <Logo />
        <div className="flex items-center space-x-2">
          <ThemeToggle />
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" color={iconColors.menu} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="backdrop-blur-md bg-white/95 dark:bg-[rgba(26,26,26,0.95)] border-gray-200 dark:border-white/5 transition-colors duration-200">
              <div className="flex flex-col space-y-4 mt-8">
                {navItems.map((item) => (
                  <NavLink 
                    key={item.name} 
                    to={item.path}
                    className={({ isActive }) => cn(
                      "flex items-center p-3 rounded-sm transition-colors duration-200",
                      isActive 
                        ? "text-black bg-gray-100 dark:text-white dark:bg-white/10 font-semibold" 
                        : "text-gray-600 dark:text-gray-300 hover:text-black hover:bg-gray-50 dark:hover:text-white dark:hover:bg-white/5"
                    )}
                  >
                    <item.icon className="h-5 w-5" color={item.color} />
                    <span className="ml-3 font-opensans text-sm tracking-wider">{item.name}</span>
                  </NavLink>
                ))}
                
                <div className="pt-4 mt-4 border-t border-gray-200 dark:border-white/10">
                  <SignOutButton variant="ghost" showIcon={false} />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full backdrop-blur-md bg-white/80 dark:bg-[rgba(26,26,26,0.8)] border-b border-gray-200 dark:border-white/5 px-6 py-2 flex items-center transition-colors duration-200">
      <Logo />
      <NavigationLinks />
      
      <div className="ml-auto flex items-center space-x-2">
        <ThemeToggle />
        <SignOutButton variant="ghost" showIcon={false} />
      </div>
    </div>
  );
};

export default Sidebar;
